<!-- Dialog Title -->
<h2 mat-dialog-title class="m-0 sg-dialog-title">
    <div class="title-content">
        <mat-icon class="title-icon">{{ isEditMode ? 'edit' : 'add_circle_outline' }}</mat-icon>
        <div class="title-text">
            <span>{{ dialogTitle }}</span>
            @if (dialogSubtitle) {
                <span class="title-subtitle">{{ dialogSubtitle }}</span>
            }
        </div>

        <!-- SG Explorer button -->
        <button mat-icon-button
                class="explorer-button ml-2"
                (click)="openSGExplorer()"
                matTooltip="Open Security Group Explorer — View groups by feature, staff member, or hat">
            <mat-icon>account_tree</mat-icon>
        </button>
    </div>
    <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="h-100-70 w-100">

    <mat-tab-group class="w-100 sg-tabs" [(selectedIndex)]="tabIndex" animationDuration="200ms">

        <!-- ═══════════════ TAB 1: CREATE / EDIT SECURITY GROUP ═══════════════ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">{{ isEditMode ? 'edit' : 'add' }}</mat-icon>
                {{ isEditMode ? 'Edit Group' : 'Create Group' }}
            </ng-template>

            <div class="view-container m-3">
            <div class="sg-form-container">

                <!-- Processing overlay -->
                @if (isProcessing) {
                    <div class="processing-overlay">
                        <mat-progress-bar mode="indeterminate" color="accent"></mat-progress-bar>
                        <span class="processing-text">{{ isEditMode ? 'Updating security group...' : 'Creating security group...' }}</span>
                    </div>
                }

                <!-- Form Header -->
                <div class="form-header">
                    <div class="form-header-left">
                        <h3 class="form-title">{{ isEditMode ? 'Edit Security Group' : 'Create a Security Group' }}</h3>
                        <p class="form-description">
                            {{ isEditMode ? 'Modify the security group configuration below.' : 'Define a security group with features and permissions.' }}
                        </p>
                    </div>
                    <div class="form-header-actions">
                        @if (isEditMode) {
                            <button mat-stroked-button (click)="resetForm()" matTooltip="Discard changes and start fresh">
                                <mat-icon>add</mat-icon>New Group
                            </button>
                        }
                        <button mat-flat-button color="accent" (click)="saveSecurityGroup()"
                            [disabled]="form.invalid || isProcessing || form.pristine">
                            <mat-icon>{{ isEditMode ? 'save' : 'check' }}</mat-icon>
                            {{ isEditMode ? 'Update' : 'Create' }}
                        </button>
                    </div>
                </div>

                <!-- Security Group Form -->
                <form [formGroup]="form" class="sg-form">

                    <!-- Section: Name -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-security-groups">badge</mat-icon>
                            Group Name
                        </h4>

                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Security Group Name</mat-label>
                            <input matInput type="text" formControlName="name" cdkFocusInitial
                                placeholder="e.g. Admin Group, Finance Read-Only" />
                            <mat-icon matPrefix>security</mat-icon>
                            @if (form.controls.name.hasError('required')) {
                                <mat-error>Name is required</mat-error>
                            }
                            @if (form.controls.name.hasError('minlength')) {
                                <mat-error>At least 3 characters</mat-error>
                            }
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Description (optional)</mat-label>
                            <textarea matInput formControlName="description" rows="2"
                                placeholder="Brief description of this security group's purpose"></textarea>
                            <mat-icon matPrefix>info_outline</mat-icon>
                            <mat-hint>Shown as a tooltip when users hover over this group</mat-hint>
                        </mat-form-field>
                    </div>

                    <!-- Section: Features -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-features">extension</mat-icon>
                            Features
                            @if (formStats.features > 0) {
                                <span class="section-badge badge-features">{{ formStats.features }}</span>
                            }
                        </h4>

                        <div class="inline-action-row">
                            <shared-searchable-select
                                class="flex-1-auto"
                                [displayValue]="false"
                                [appearance]="'outline'"
                                [classes]="['w-100']"
                                [label]="'Search and select features'"
                                [data]="searchableFeatures"
                                [control]="featuresControl"
                                (selected)="onSelectFeature($event)"
                                [multiple]="true"
                            ></shared-searchable-select>

                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="openFeatureSelector()" matTooltip="Browse and select features">
                                <mat-icon>flare</mat-icon>Browse
                            </button>
                        </div>

                        @if (getSelectedFeatureItems().length > 0) {
                            <div class="selected-chips">
                                @for (item of getSelectedFeatureItems(); track item.value) {
                                    <span class="selected-chip chip-feature chip-clickable"
                                          [matTooltip]="features[item.value]?.description || 'Click to configure permissions'">
                                        <mat-icon class="chip-icon">extension</mat-icon>
                                        {{ item.display }}
                                        <mat-icon class="chip-remove"
                                                  (click)="removeFeature(item.value); $event.stopPropagation()"
                                                  matTooltip="Remove feature">close</mat-icon>
                                    </span>
                                }
                            </div>
                        }
                    </div>

                    <!-- Section: Permissions Per Feature -->
                    @if (formStats.features > 0) {
                        <div class="form-section">
                            <h4 class="section-title">
                                <mat-icon class="entity-permissions">vpn_key</mat-icon>
                                Permissions by Feature
                                @if (formStats.permissions > 0) {
                                    <span class="section-badge badge-permissions">{{ formStats.permissions }}</span>
                                }
                            </h4>

                            <div class="permissions-grid">
                                @for (pair of selectedFeaturesWithoutSystemFeatures | keyvalue; track pair.key) {
                                    <div class="perm-feature-block">
                                        @if (features[pair.key]?.permissions?.length) {
                                        <mat-form-field appearance="outline" class="w-100">
                                            <mat-label>{{ features[pair.key]?.name }}</mat-label>
                                            <mat-icon matPrefix class="perm-feature-icon"
                                                      [matTooltip]="features[pair.key]?.description || ''">extension</mat-icon>
                                            <mat-select multiple [(ngModel)]="selectedFeatures[pair.key].permissions"
                                                         [ngModelOptions]="{standalone: true}"
                                                         (ngModelChange)="form.markAsDirty()">
                                                <mat-select-trigger>
                                                    {{ (selectedFeatures[pair.key]?.permissions?.[0] || '') | normalize }}
                                                    @if ((selectedFeatures[pair.key]?.permissions?.length || 0) > 1) {
                                                        <span class="perm-additional">
                                                            (+{{ (selectedFeatures[pair.key].permissions.length || 0) - 1 }}
                                                            {{ selectedFeatures[pair.key].permissions.length === 2 ? 'other' : 'others' }})
                                                        </span>
                                                    }
                                                </mat-select-trigger>
                                                @for (perm of features[pair.key]?.permissions; track perm) {
                                                    <mat-option [value]="perm"
                                                               [matTooltip]="features[pair.key]?.permissionDescriptions?.[perm] || ''"
                                                               matTooltipPosition="right">
                                                        {{ perm | normalize }}
                                                    </mat-option>
                                                }
                                            </mat-select>
                                        </mat-form-field>
                                        } @else {
                                        <div class="perm-no-permissions">
                                            <mat-icon class="perm-feature-icon"
                                                      [matTooltip]="features[pair.key]?.description || ''">extension</mat-icon>
                                            <span class="perm-feature-name">{{ features[pair.key]?.name }}</span>
                                            <span class="perm-no-perms-label">Access only — no granular permissions</span>
                                        </div>
                                        }

                                        @if ((selectedFeatures[pair.key]?.permissions?.length || 0) > 0) {
                                            <div class="perm-chips">
                                                @for (perm of selectedFeatures[pair.key].permissions; track perm) {
                                                    <span class="perm-badge"
                                                          [matTooltip]="features[pair.key]?.permissionDescriptions?.[perm] || ''">
                                                        {{ perm | normalize }}
                                                    </span>
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    }

                </form>
            </div>
            </div>

        </mat-tab>


        <!-- ═══════════════ TAB 2: EXISTING SECURITY GROUPS TABLE ═══════════════ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">list</mat-icon>
                Existing Groups
                @if (dataSource.data.length > 0) {
                    <span class="tab-badge">{{ dataSource.data.length }}</span>
                }
            </ng-template>

            <div class="view-container m-3">
            <div class="sg-list-container">
                <div class="list-header">
                    <h3 class="list-title">Security Groups</h3>
                    <mat-form-field class="search-field" appearance="outline">
                        <mat-label>Filter security groups</mat-label>
                        <input matInput (keyup)="applyFilter($event)" placeholder="Search by name, feature, hat, staff member..." />
                        <mat-icon matSuffix>search</mat-icon>
                    </mat-form-field>
                </div>

                @if (dataSource.data.length === 0) {
                    <div class="empty-state">
                        <mat-icon>security</mat-icon>
                        <p>No security groups created yet</p>
                        <span>Create your first security group in the form tab</span>
                    </div>
                } @else {
                    <mat-table [dataSource]="dataSource" class="sg-table">

                        <ng-container [matColumnDef]="column.columnDef" *ngFor="let column of columnsWithoutActions">
                            <mat-header-cell *matHeaderCellDef>{{ column.header }}</mat-header-cell>
                            <mat-cell *matCellDef="let row">{{ column.cell(row) }}</mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="actions">
                            <mat-header-cell *matHeaderCellDef class="actions-col">Actions</mat-header-cell>
                            <mat-cell *matCellDef="let row" class="actions-col">
                                <button mat-icon-button color="primary" matTooltip="View details"
                                    (click)="viewSecurityGroupDetails(row.key)">
                                    <mat-icon>visibility</mat-icon>
                                </button>
                                <button mat-icon-button color="primary" matTooltip="Edit group"
                                    (click)="editSecurityGroup(row)">
                                    <mat-icon>edit</mat-icon>
                                </button>
                                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions">
                                    <mat-icon>more_vert</mat-icon>
                                </button>
                                <mat-menu #menu="matMenu">
                                    <button mat-menu-item (click)="viewSecurityGroupDetails(row.key)">
                                        <mat-icon>visibility</mat-icon>
                                        <span>View Details</span>
                                    </button>
                                    <button mat-menu-item (click)="editSecurityGroup(row)">
                                        <mat-icon>edit</mat-icon>
                                        <span>Edit</span>
                                    </button>
                                    <button mat-menu-item class="delete-action" (click)="deleteSecurityGroup(row)">
                                        <mat-icon color="warn">delete</mat-icon>
                                        <span>Delete</span>
                                    </button>
                                </mat-menu>
                            </mat-cell>
                        </ng-container>

                        <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
                        <mat-row matRipple *matRowDef="let row; columns: displayedColumns;"
                            (dblclick)="editSecurityGroup(row)"></mat-row>
                    </mat-table>
                }
            </div>
            </div>
        </mat-tab>

    </mat-tab-group>

</mat-dialog-content>

<mat-dialog-actions align="end">
    @if (isEditMode) {
        <button mat-button (click)="resetForm()">
            <mat-icon>add</mat-icon>New Group
        </button>
    }
    <button mat-button mat-dialog-close>Close</button>
    <button mat-flat-button color="accent" (click)="saveSecurityGroup()"
        [disabled]="form.invalid || isProcessing || form.pristine">
        <mat-icon>{{ isEditMode ? 'save' : 'check' }}</mat-icon>
        {{ isEditMode ? 'Update Group' : 'Create Group' }}
    </button>
</mat-dialog-actions>